import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/lib/auth';

export interface SampleRequirement {
  id: string;
  job_id: string;
  specification_id: string;
  item_type: 'proofs' | 'samples';
  quantity: number;
  delivery_template_id: string | null;
  attention_of: string | null;
  notes: string | null;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface SampleRequirementInput {
  specification_id: string;
  item_type: 'proofs' | 'samples';
  quantity: number;
  delivery_template_id: string | null;
  attention_of: string | null;
  notes?: string | null;
}

export function useJobSampleRequirements(jobId: string) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [requirements, setRequirements] = useState<SampleRequirement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRequirements = useCallback(async () => {
    if (!jobId) {
      setRequirements([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('job_sample_requirements')
        .select('*')
        .eq('job_id', jobId)
        .order('created_at', { ascending: true });

      if (fetchError) throw fetchError;

      setRequirements((data || []) as SampleRequirement[]);
    } catch (err) {
      console.error('Error fetching sample requirements:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch sample requirements');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchRequirements();
  }, [fetchRequirements]);

  const addRequirement = async (input: SampleRequirementInput): Promise<SampleRequirement | null> => {
    if (!user) {
      toast({
        title: 'Error',
        description: 'You must be logged in to add requirements',
        variant: 'destructive',
      });
      return null;
    }

    try {
      const { data, error: insertError } = await supabase
        .from('job_sample_requirements')
        .insert({
          job_id: jobId,
          specification_id: input.specification_id,
          item_type: input.item_type,
          quantity: input.quantity,
          delivery_template_id: input.delivery_template_id,
          attention_of: input.attention_of,
          notes: input.notes || null,
          created_by_user_id: user.id,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      const newRequirement = data as SampleRequirement;
      setRequirements(prev => [...prev, newRequirement]);

      return newRequirement;
    } catch (err) {
      console.error('Error adding sample requirement:', err);
      toast({
        title: 'Error',
        description: 'Failed to add sample requirement',
        variant: 'destructive',
      });
      return null;
    }
  };

  const updateRequirement = async (id: string, updates: Partial<SampleRequirementInput>): Promise<boolean> => {
    try {
      const { error: updateError } = await supabase
        .from('job_sample_requirements')
        .update(updates)
        .eq('id', id);

      if (updateError) throw updateError;

      setRequirements(prev =>
        prev.map(req => (req.id === id ? { ...req, ...updates } : req))
      );

      return true;
    } catch (err) {
      console.error('Error updating sample requirement:', err);
      toast({
        title: 'Error',
        description: 'Failed to update sample requirement',
        variant: 'destructive',
      });
      return false;
    }
  };

  const deleteRequirement = async (id: string): Promise<boolean> => {
    try {
      const { error: deleteError } = await supabase
        .from('job_sample_requirements')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      setRequirements(prev => prev.filter(req => req.id !== id));

      return true;
    } catch (err) {
      console.error('Error deleting sample requirement:', err);
      toast({
        title: 'Error',
        description: 'Failed to delete sample requirement',
        variant: 'destructive',
      });
      return false;
    }
  };

  return {
    requirements,
    loading,
    error,
    refetch: fetchRequirements,
    addRequirement,
    updateRequirement,
    deleteRequirement,
  };
}
